import { api } from "@/lib/api";

export const pushSupported = () =>
  typeof navigator !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const isIOS = () =>
  typeof navigator !== "undefined" &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

export const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true);

const IOS_HOMESCREEN_MSG =
  "On iPhone or iPad, add this app to your Home Screen first (tap Share, then \u201CAdd to Home Screen\u201D) and open it from there to turn on notifications.";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/sw.js");
  } catch {
    return null;
  }
}

export async function enablePush() {
  if (isIOS() && !isStandalone()) throw new Error(IOS_HOMESCREEN_MSG);
  if (!pushSupported()) throw new Error("This device doesn't support notifications.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notifications are blocked. Please allow them in your browser settings.");
  const reg = (await registerServiceWorker()) || (await navigator.serviceWorker.ready);
  await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/public-key");
  if (!data.publicKey) throw new Error("Notifications aren't configured on the server yet.");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return true;
}

export async function ensurePushSubscribed() {
  try {
    if (!pushSupported() || Notification.permission !== "granted") return false;
    const reg = (await registerServiceWorker()) || (await navigator.serviceWorker.ready);
    await navigator.serviceWorker.ready;
    const { data } = await api.get("/push/public-key");
    if (!data.publicKey) return false;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }
    await api.post("/push/subscribe", sub.toJSON());
    return true;
  } catch {
    return false;
  }
}

export async function disablePush() {
  let endpoint = "disabled-no-active-subscription-endpoint";
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      endpoint = sub.endpoint;
      await sub.unsubscribe();
    }
  } catch {
    /* ignore */
  }
  await api.delete("/push/unsubscribe", { data: { endpoint } });
  return true;
}
